import type { EditableProposalForm } from "@playbookai/player-ai-client";

interface ApprovalImpactAction {
  id: string;
  title: string;
  service: string;
  target: string | undefined;
  form: EditableProposalForm;
  values: Record<string, unknown>;
}

/** Describe saved actions and local edits without guessing provider side effects. */
export function proposalApprovalImpact(
  actions: ApprovalImpactAction[],
  excludedCount: number,
) {
  const counts = new Map<string, number>();
  for (const action of actions) {
    counts.set(action.service, (counts.get(action.service) ?? 0) + 1);
  }
  return {
    headline: [...counts]
      .map(
        ([service, count]) =>
          `${count} ${service} ${count === 1 ? "action" : "actions"}`,
      )
      .join(", "),
    detail: [
      "These actions will change your connected services using your accounts. Each action can succeed or fail independently; completed changes are not automatically undone.",
      excludedCount > 0
        ? `${excludedCount} unchecked ${excludedCount === 1 ? "action will" : "actions will"} be rejected and will not run.`
        : "",
    ]
      .filter(Boolean)
      .join(" "),
    actions: actions.map((action) => {
      const edits = action.form.fields
        .filter(
          (field) =>
            field.editable &&
            JSON.stringify(action.values[field.path]) !==
              JSON.stringify(action.form.values[field.path]),
        )
        .map(
          (field) =>
            `${field.label}: ${impactValue(action.form.values[field.path])} → ${impactValue(action.values[field.path])}`,
        );
      return {
        id: action.id,
        title: `${action.service} · ${action.title}`,
        detail: [
          action.target ? `Target: ${action.target}.` : "",
          edits.length > 0
            ? `Edits to the suggestion: ${edits.join("; ")}.`
            : "",
        ]
          .filter(Boolean)
          .join(" "),
      };
    }),
    confirmLabel: `Approve and run ${actions.length} ${actions.length === 1 ? "action" : "actions"}`,
  };
}

/** Format a bounded confirmation summary without changing the underlying draft. */
function impactValue(value: unknown): string {
  const text =
    value === undefined || value === null || value === ""
      ? "(empty)"
      : typeof value === "string"
        ? value
        : JSON.stringify(value);
  return text.length > 160 ? `${text.slice(0, 160)}…` : text;
}
